import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatBadge } from "blocks-nextjs/blocks-react/components/Badge/Badge";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatContextualMenu } from "blocks-nextjs/blocks-react/components/ContextualMenu/ContextualMenu";
import { CatIconAddCircle } from "blocks-nextjs/blocks-react/components/Icons/AddCircle/AddCircle";
import { CatIconChevronDown } from "blocks-nextjs/blocks-react/components/Icons/ChevronDown/ChevronDown";
import { CatIconSuccessFilled } from "blocks-nextjs/blocks-react/components/Icons/SuccessFilled/SuccessFilled";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatSearchForm } from "blocks-nextjs/blocks-react/components/SearchForm/SearchForm";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAssetImageIcon } from "../../pages/Projects/ProjectDetails.helper";
import { CaasRoute } from "../../routes";
import { useCountAssetsQuery, useSearchAssetsQuery } from "../../services/query/assets-v2.query";
import { useGetProjectDetailsQuery } from "../../services/query/create-project-api.query";
import { useUpdateProjectAssetDetails } from "../../services/query/updates-project-asset-details.query";
import useGlobalStore from "../../store/useGlobalStore";
import useNewProjectStore from "../../store/useNewProjectStore";
import { AssetPropertiesForSorting } from "../../swagger/assets-v2/models/AssetPropertiesForSorting";
import { FilterType } from "../../swagger/assets-v2/models/FilterType";
import { Variant } from "../GlobalNotification/GlobalNotification";
export var AssetType;
(function (AssetType) {
    AssetType["ESS"] = "ESS";
    AssetType["MACHINE"] = "MACHINE";
})(AssetType || (AssetType = {}));
export const AddAssetsListCard = forwardRef(({ setAddedAssetListInfo, setAssetChangeStatus }, ref) => {
    const [sortOrder, setSortOrder] = useState("ASC");
    const [searchTerm, setSearchTerm] = useState("");
    const [newAssetList, setNewAssetList] = useState([]);
    //initialAssetList
    const [initialAssetList, setInitialAssetList] = useState([]);
    const navigate = useNavigate();
    const { primaryPartyType, setGlobalNotification, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        setGlobalNotification: state.setGlobalNotification,
        userPartyNumber: state.userPartyNumber
    }));
    const { projectId } = useNewProjectStore((state) => ({
        projectId: state.projectId
    }));
    const queryParams = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
    };
    const { data: projectDetailsResponse, isFetching: isProjectDetailsFetching, isLoading: isProjectDetailsLoading, refetch: fetchProjectDetails } = useGetProjectDetailsQuery(projectId, queryParams);
    const queryParamsUpdateProjectAssetInfo = {
        ...queryParams,
        operationType: "create"
    };
    const queryParamsRemoveProjectAssetInfo = {
        ...queryParams,
        operationType: "remove"
    };
    const { mutate } = useUpdateProjectAssetDetails(projectId, queryParamsUpdateProjectAssetInfo);
    const { mutate: removeMutate } = useUpdateProjectAssetDetails(projectId, queryParamsRemoveProjectAssetInfo);
    useEffect(() => {
        if (projectId) {
            fetchProjectDetails();
        }
    }, [projectId, fetchProjectDetails]);
    const saveAddRemoveAssets = useCallback(() => {
        //find the newly added assets by comparing newAssetList with initialAssetList
        const newlyAddedAssetList = newAssetList.filter((newAsset) => !initialAssetList.some((initialAsset) => initialAsset.assetSourceId === newAsset.assetSourceId &&
            initialAsset.assetSerialNumber === newAsset.assetSerialNumber &&
            initialAsset.assetSourceType === newAsset.assetSourceType));
        //find the removed assets by comparing initialAssetList with newAssetList
        const removedAssetList = initialAssetList.filter((initialAsset) => !newAssetList.some((newAsset) => initialAsset.assetSourceId === newAsset.assetSourceId &&
            initialAsset.assetSerialNumber === newAsset.assetSerialNumber &&
            initialAsset.assetSourceType === newAsset.assetSourceType));
        const hasAddedAssets = newlyAddedAssetList.length > 0;
        const hasRemovedAssets = removedAssetList.length > 0;
        if (!projectId || (!hasAddedAssets && !hasRemovedAssets)) {
            return;
        }
        const getSuccessMessage = () => {
            if (hasAddedAssets && hasRemovedAssets) {
                return "Success. Assets have been added and removed successfully";
            }
            if (hasAddedAssets) {
                return "Success. Assets have been added successfully";
            }
            return "Success. Assets have been removed successfully";
        };
        const getErrorMessage = () => {
            if (hasAddedAssets && hasRemovedAssets) {
                return "Error. Failed to add and remove assets. Please try later.";
            }
            if (hasAddedAssets) {
                return "Error. Failed to add assets. Please try later.";
            }
            return "Error. Failed to remove assets. Please try later.";
        };
        const projectAssetsUpdateRequest = {
            assetDetails: newlyAddedAssetList.map((asset) => ({
                assetId: asset.assetId ?? asset.assetSourceId,
                assetMetadata: {
                    model: asset.assetMetadata?.model,
                    modelYear: asset.assetMetadata?.modelYear,
                    productFamily: {
                        code: asset.assetMetadata?.productFamily?.code,
                        iconUrl: asset.assetMetadata?.productFamily?.iconUrl,
                        name: asset.assetMetadata?.productFamily?.name
                    }
                },
                assetName: asset.assetName ?? asset.assetSerialNumber,
                assetSerialNumber: asset.assetSerialNumber,
                assetSourceId: asset.assetSourceId,
                assetSourceType: asset.assetSourceType ?? AssetType.MACHINE
            }))
        };
        const removedProjectAssetsUpdateRequest = {
            assetDetails: removedAssetList.map((asset) => ({
                assetId: asset.assetId ?? asset.assetSourceId
            }))
        };
        // if there are only removed assets, call removeMutate only.
        if (!hasAddedAssets && hasRemovedAssets) {
            removeMutate(removedProjectAssetsUpdateRequest, {
                onError: () => {
                    setGlobalNotification({
                        message: getErrorMessage(),
                        showNotification: true,
                        type: Variant.error
                    });
                    navigate(CaasRoute.Home);
                },
                onSuccess: () => {
                    setGlobalNotification({
                        message: getSuccessMessage(),
                        showNotification: true,
                        type: Variant.success
                    });
                    navigate(CaasRoute.Home);
                }
            });
            return;
        }
        mutate(projectAssetsUpdateRequest, {
            onError: () => {
                setGlobalNotification({
                    message: getErrorMessage(),
                    showNotification: true,
                    type: Variant.error
                });
                navigate(CaasRoute.Home);
            },
            onSuccess: () => {
                if (!hasRemovedAssets) {
                    setGlobalNotification({
                        message: getSuccessMessage(),
                        showNotification: true,
                        type: Variant.success
                    });
                    navigate(CaasRoute.Home);
                    return;
                }
                removeMutate(removedProjectAssetsUpdateRequest, {
                    onError: () => {
                        setGlobalNotification({
                            message: getErrorMessage(),
                            showNotification: true,
                            type: Variant.error
                        });
                        navigate(CaasRoute.Home);
                    },
                    onSuccess: () => {
                        setGlobalNotification({
                            message: getSuccessMessage(),
                            showNotification: true,
                            type: Variant.success
                        });
                        navigate(CaasRoute.Home);
                    }
                });
            }
        });
    }, [initialAssetList, mutate, newAssetList, projectId, removeMutate, setGlobalNotification, navigate]);
    useImperativeHandle(ref, () => ({
        saveAddRemoveAssets
    }), [saveAddRemoveAssets]);
    useEffect(() => {
        if (projectDetailsResponse?.assetDetails) {
            const existingAssets = projectDetailsResponse.assetDetails.map((asset) => ({
                assetId: asset.assetId,
                assetMetadata: {
                    model: asset.assetMetadata?.model,
                    modelYear: asset.assetMetadata?.modelYear,
                    productFamily: {
                        code: asset.assetMetadata?.productFamily?.code,
                        iconUrl: asset.assetMetadata?.productFamily?.iconUrl,
                        name: asset.assetMetadata?.productFamily?.name
                    }
                },
                assetName: asset.assetName,
                assetSerialNumber: asset.assetSerialNumber ?? "",
                assetSourceId: asset.assetSourceId,
                assetSourceType: asset.assetSourceType?.toUpperCase()
            }));
            //   "assetSourceType": "CHARGING_STATION", "assetSourceType": "ENERGY_SOURCE" remove these 2 types from the existing list
            const filteredexistingAssets = existingAssets.filter((asset) => asset.assetSourceType !== "CHARGING_STATION" && asset.assetSourceType !== "ENERGY_SOURCE");
            setNewAssetList(filteredexistingAssets);
            setInitialAssetList(filteredexistingAssets);
        }
    }, [projectDetailsResponse]);
    const isSearchEnabled = searchTerm.trim().length >= 3;
    const requestBody = isSearchEnabled
        ? {
            filters: [
                {
                    isCaseSensitive: false,
                    propertyName: "serialNumber",
                    type: FilterType.CONTAINS,
                    value: searchTerm.trim()
                }
            ],
            logicalExpression: "$0"
        }
        : undefined;
    const { data: machineAssetList, isError: isSearchError, isFetching: isAssetSearchFetching, isPending: isAssetSearchLoading } = useSearchAssetsQuery({
        enabled: isSearchEnabled,
        paramsList: {
            ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber }),
            orderBy: [sortOrder],
            sortBy: [AssetPropertiesForSorting.SERIAL_NUMBER]
        },
        requestBody
    });
    const { data: countAssetsData } = useCountAssetsQuery({
        enabled: isSearchEnabled,
        paramsList: {
            ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
        },
        requestBody
    });
    const preparedMachineAssetList = useMemo(() => {
        if (!machineAssetList?.assets) {
            return [];
        }
        return machineAssetList.assets.map((asset) => ({
            assetMetadata: {
                model: asset.metadata?.model,
                modelYear: asset.metadata?.modelYear,
                productFamily: {
                    code: asset.metadata?.productFamily?.code,
                    iconUrl: asset.metadata?.icon?.imageUrl,
                    name: asset.metadata?.productFamily?.name
                }
            },
            assetName: asset.metadata?.name ?? "",
            assetSerialNumber: asset.metadata?.serialNumber ?? "",
            assetSourceId: asset.id,
            assetSourceType: AssetType.MACHINE
        }));
    }, [isSearchEnabled, machineAssetList, newAssetList]);
    useEffect(() => {
        setAddedAssetListInfo(newAssetList);
    }, [newAssetList, setAddedAssetListInfo]);
    useEffect(() => {
        const hasAddedAssets = newAssetList.some((newAsset) => !initialAssetList.some((initialAsset) => initialAsset.assetSourceId === newAsset.assetSourceId &&
            initialAsset.assetSerialNumber === newAsset.assetSerialNumber &&
            initialAsset.assetSourceType === newAsset.assetSourceType));
        const hasRemovedAssets = initialAssetList.some((initialAsset) => !newAssetList.some((newAsset) => initialAsset.assetSourceId === newAsset.assetSourceId &&
            initialAsset.assetSerialNumber === newAsset.assetSerialNumber &&
            initialAsset.assetSourceType === newAsset.assetSourceType));
        setAssetChangeStatus?.({ hasAddedAssets, hasRemovedAssets });
    }, [initialAssetList, newAssetList, setAssetChangeStatus]);
    const handleSearch = (term) => {
        setSearchTerm(term ?? "");
    };
    const listRef = useRef(null);
    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el)
            return;
        const { clientHeight, scrollHeight, scrollTop } = el;
        if (scrollHeight - scrollTop - clientHeight <= 1) {
            // Implement infinite scroll logic here if needed
            return;
        }
    }, []);
    const getSortOptions = () => {
        return (_jsx("div", { children: _jsx(CatContextualMenu, { position: "bottom", children: _jsxs(React.Fragment, { children: [_jsxs(CatButton, { hideText: true, size: "xs", slot: "trigger", variant: "ghost", children: ["Menu", _jsx(CatIconChevronDown, { slot: "after" })] }), _jsxs(CatList, { className: "w-44", "data-test": "sort-assets-list", onBlSelect: (e) => {
                                const itemInfo = e.detail.value;
                                setSortOrder(itemInfo.value);
                            }, children: [_jsx(CatListItem, { "data-test": "sort-assets-ascending", value: {
                                        label: "Ascending by S/N",
                                        value: "ASC"
                                    } }), _jsx(CatListItem, { "data-test": "sort-assets-descending", value: {
                                        label: "Descending by S/N",
                                        value: "DESC"
                                    } })] })] }, ".0") }) }));
    };
    const getSortName = () => {
        return sortOrder === "ASC" ? "S/N - A to Z " : "S/N - Z to A";
    };
    const displayedAssetList = isSearchEnabled ? preparedMachineAssetList : newAssetList;
    const isAssetAdded = useCallback((asset) => newAssetList.some((a) => a.assetSourceId === asset.assetSourceId &&
        a.assetSerialNumber === asset.assetSerialNumber &&
        a.assetSourceType === asset.assetSourceType), [newAssetList]);
    const toggleAsset = useCallback((asset) => {
        setNewAssetList((prev) => {
            //check if the asset is already in the list
            const isMatch = (a) => a.assetSourceId === asset.assetSourceId &&
                a.assetSerialNumber === asset.assetSerialNumber &&
                a.assetSourceType === asset.assetSourceType;
            // If asset is already added, remove it. Otherwise, add it to the list.
            if (prev.some(isMatch)) {
                return prev.filter((a) => !isMatch(a));
            }
            return [
                ...prev,
                {
                    assetMetadata: asset.assetMetadata,
                    assetName: asset.assetName,
                    assetSerialNumber: asset.assetSerialNumber,
                    assetSourceId: asset.assetSourceId,
                    assetSourceType: asset.assetSourceType
                }
            ];
        });
    }, []);
    const emptyStateMessage = useMemo(() => {
        if ((isAssetSearchLoading && isAssetSearchFetching) ||
            (isProjectDetailsLoading && isProjectDetailsFetching)) {
            return (_jsx("li", { className: "flex flex-col items-center justify-center h-full text-center text-sm text-gray-500", children: _jsx("p", { children: "Loading assets..." }) }));
        }
        if (isSearchError) {
            return (_jsxs("li", { className: "flex flex-col items-center justify-center h-full text-center text-sm text-gray-500", children: [_jsx("p", { children: "Error: Data failed to load." }), _jsx("p", { children: "Please try again later." })] }));
        }
        if (!isSearchEnabled && newAssetList.length === 0) {
            return (_jsx("li", { className: "flex flex-col items-center justify-center h-full text-center text-sm text-gray-500 px-[16px]", children: _jsx("p", { children: "Add the machine assets (machines) you employ and the charging schedule you use to manage your electric fleet." }) }));
        }
        if (!isAssetSearchFetching && displayedAssetList.length === 0) {
            return (_jsxs("li", { className: "flex flex-col items-center justify-center h-full text-center text-sm text-gray-500", children: [_jsx("p", { children: "No matching assets or schedules found." }), _jsx("p", { children: "Try adjusting your filters or search terms." })] }));
        }
        return null;
    }, [
        displayedAssetList.length,
        isAssetSearchFetching,
        isAssetSearchLoading,
        isProjectDetailsFetching,
        isProjectDetailsLoading,
        isSearchEnabled,
        isSearchError,
        newAssetList.length
    ]);
    return (_jsxs("section", { className: "flex-1 flex flex-col gap-[16px] bg-[#FFFFFF] rounded-[4px] border-[#CCCCCC] border py-[16px]", children: [_jsx("p", { className: "font-semibold text-sm leading-5 tracking-normal px-[16px]", children: "Available assets" }), _jsx("div", { className: "px-[12px]", children: _jsx(CatSearchForm, { arialListLabel: "Search", "data-test": "search-asset-serial-number", isEmpty: true, label: "Label", onBlChange: (e) => {
                        handleSearch(e.detail.value);
                    }, placeholder: "Add Machine Asset Serial #" }) }), _jsxs("section", { children: [_jsxs("header", { className: "flex justify-between items-center h-[32px] px-[12px]  border-b-[0.2px] border-[#CCCCCC]", children: [_jsxs("div", { className: "flex items-center", children: [_jsx("span", { className: "font-semibold text-sm leading-5 tracking-normal", children: `Sort By ${getSortName()}` }), _jsx("span", { children: getSortOptions() })] }), _jsx("div", { children: _jsx(CatBadge, { "data-test": "assets-count-badge", text: countAssetsData?.count.toString() ?? "0", variant: "blue" }) })] }), _jsxs("ul", { className: "flex flex-col max-h-[230px] h-[230px] overflow-y-auto", onScroll: handleScroll, ref: listRef, children: [emptyStateMessage, displayedAssetList.map((asset, index) => {
                                const added = isAssetAdded(asset);
                                const isExistingAsset = initialAssetList.some((initialAsset) => initialAsset.assetId !== undefined && initialAsset.assetId === asset.assetId);
                                const assetDisplayName = isExistingAsset
                                    ? asset.assetName
                                    : asset.assetSourceId?.replace(/&pipe;$/g, "").replace(/&pipe;/g, " | ");
                                return (_jsxs("li", { className: "flex items-center gap-[16px] min-h-[64px] px-[16px] py-[8px] border-b-[0.2px] border-[#CCCCCC] last:border-0", children: [_jsx("figure", { "aria-hidden": "true", children: _jsx("img", { alt: "Assets", className: "w-[48px] min-h-[48px] min-w-[48px]", "data-test": `asset-${index}-image`, src: asset.assetMetadata?.productFamily?.iconUrl ??
                                                    getAssetImageIcon({
                                                        assetId: "5fe212ea-8fa8-4d71-a020-d3e4b504ee3amm"
                                                    }) }) }), _jsxs("div", { className: "grow", children: [_jsxs("p", { className: "font-bold tracking-normal align-middle text-sm", "data-test": `asset-${index}-id`, children: [assetDisplayName, " "] }), _jsx("p", { className: "font-normal text-xs leading-4 tracking-normal align-middle", "data-test": `asset-${index}-serial-number`, children: asset.assetSerialNumber })] }), _jsx("div", { className: "asset-item__actions", children: _jsx(CatButton, { "data-test": `asset-${index}-button`, hideText: true, onClick: () => toggleAsset(asset), size: "sm", variant: "ghost", children: _jsxs(React.Fragment, { children: [added ? (_jsx(CatIconSuccessFilled, { color: "information", iconTitle: "Remove asset", size: "lg", slot: "before" })) : (_jsx(CatIconAddCircle, { color: "information", iconTitle: "Asset added", size: "lg", slot: "before" })), added ? "Remove" : "Add"] }, ".0") }) })] }, asset.assetSourceId));
                            })] })] })] }));
});
AddAssetsListCard.displayName = "AddAssetsListCard";
export default AddAssetsListCard;
